import moment from 'moment';
import { DEFAULT_DATE_FORMAT, LOCAL_TIME_FORMAT, DEFAULT_DATE_TIME_FORMAT, DATE_TIME_FORMAT, FR_CA_LOCALE, EN_CA_LOCALE, DD_MM_YYYY_MILITARY_TIME_FORMAT, MM_DD_YYYY_NON_MILITARY_TIME_FORMAT, DATE_FORMAT_YYYY_MM_DD, DATE_FORMAT_YYYY, DATE_FORMAT_MMMM_DD_HH_MM_A, DATE_FORMAT_CA_YYYY_MM_DD, DATE_FORMAT_MMMM_DD } from './Constants';
import { Utilities } from './Utilities';
import { DateFormatForLocale, DateFragmentType } from './Enums';
import { LocaleCodes, defaultTranslateFn } from "../common";
export class DateUtilities {
    /**
     * @description Returns true for a valid date
     * @param date A Date object
     */
    static isValidDate(date) {
        return date ? moment(date).isValid() : false;
    }
    /**
     * @description returns true if the date is today
     * @param dateValue date to validate
     * @param isUtc [optional] flag to represent the passed date is in utc format.
     */
    static isToday(dateValue, isUtc) {
        if (isUtc) {
            const utcDate = moment(dateValue)
                .utc()
                .format(DATE_FORMAT_YYYY_MM_DD);
            return moment(utcDate).isSame(moment(), 'd');
        }
        return moment(dateValue).isSame(moment(), 'd');
    }
    /**
     * @description returns true if the date is tomorrow
     * @param dateValue date to validate
     * @param isUtc [optional] flag to represent the passed date is in utc format.
     */
    static isTomorrow(dateValue, isUtc) {
        if (isUtc) {
            const utcDate = moment(dateValue)
                .utc()
                .format(DATE_FORMAT_YYYY_MM_DD);
            return moment(utcDate).isSame(moment().add(1, 'days'), 'd');
        }
        return moment(dateValue).isSame(moment().add(1, 'days'), 'd');
    }
    /**
     * @description indicates if a date is on due date or close to due date
     * @param dateValue
     */
    static isCloseDueOrOverdueDate(dateValue) {
        const days = DateUtilities.daysPastToday(dateValue);
        return days <= 1;
    }
    /**
     * @description calculates days from/to the current date, ignoring time info i.e. 10/10 23:00:00 and 10/11 01:00:00 are 1 day apart
     * @param dateValue
     * @returns days past current date, returns negative value if dateValue is before current date
     */
    static daysPastToday(dateValue) {
        return moment(dateValue)
            .startOf('day')
            .diff(moment().startOf('day'), 'days');
    }
    /**
     * @description returns proper string for ECD tooltip on Job component
     * @param dateValue date to use in calculation
     */
    static formatECDTooltip(dateValue, translateFn = defaultTranslateFn) {
        const days = DateUtilities.daysPastToday(dateValue);
        if (days === 0) {
            return translateFn('Due Today');
        }
        if (days > 0) {
            return days === 1
                ? translateFn('Due In 1 Day')
                : translateFn('Due In {{days}} Days', { days: days });
        }
        else
            return days === -1
                ? translateFn('1 Day Overdue')
                : translateFn('{{days}} Days Overdue', { days: Math.abs(days) });
    }
    /**
     * @description returns the formatted date by converting into UTC
     * @param dateValue date to format
     * @param format format to return eg : DD/MM/YYYY'
     */
    static formatDateToUTC(dateValue, format = DEFAULT_DATE_FORMAT) {
        return dateValue ? moment(moment(dateValue).utc()).format(format) : '';
    }
    /**
     * @description returns the formatted date
     * @param dateValue date to format
     * @param format format to return eg : DD/MM/YYYY'
     */
    static formatDate(dateValue, format = DEFAULT_DATE_FORMAT) {
        return dateValue ? moment(dateValue).format(format) : '';
    }
    /**
     * @description returns the formatted time
     * @param timeValue time to format
     * @param format format to return eg : based on locale it return  'h:mm A' or 'HH:mm'
     */
    static formatTime(timeValue, format = LOCAL_TIME_FORMAT) {
        return moment(timeValue).format(format);
    }
    /**
     * @description returns the formatted date
     * @param datetimeValue datetime to format eg : Dec 02, 2020 | 11:15 AM
     * @param format format to return eg : YYYY-MM-DDTHH:mm:ss'
     */
    static formatDateTime(datetimeValue, format = DEFAULT_DATE_TIME_FORMAT) {
        return moment(datetimeValue, DATE_TIME_FORMAT).format(format);
    }
    /**
     * @description returns the formatted datetime based locale:
     *              - Given fr-CA and es-ES locale, the date time format should be DD/MM/YYYY HH:MM, where time use 24 hr format
     *              - For all other locale, the date time format should be same as en-US locale format, like MM/DD/YYYY h:mm AM or PM (12 hr format)
     *
     * @param {string} datetime - datetime value string like "2021-04-26T02:50:51-07:00"
     * @param {string=} locale - locale value string like "en, en-US, fr, fr-FR, es, es-ES"
     * @return {string} returns the formatted datetime based locale
     */
    static formatToShortDateTime(datetime, locale) {
        const currentLocale = locale || Utilities.getLocale();
        const defaultLocale = LocaleCodes.EN_US;
        switch (currentLocale.toLowerCase()) {
            case LocaleCodes.FR_CA.toLowerCase():
            case LocaleCodes.FR.toLowerCase():
            case LocaleCodes.ES_ES.toLowerCase():
            case LocaleCodes.ES.toLowerCase():
                return DateUtilities.formatDateTimeLocalized(datetime, currentLocale, DD_MM_YYYY_MILITARY_TIME_FORMAT);
            case LocaleCodes.EN_US.toLowerCase():
            case LocaleCodes.EN.toLowerCase():
            default:
                return DateUtilities.formatDateTimeLocalized(datetime, defaultLocale, MM_DD_YYYY_NON_MILITARY_TIME_FORMAT);
        }
    }
    static formatDateTimeLocalized(datetime, locale, formatString) {
        return moment(datetime)
            .locale(locale)
            .format(formatString);
    }
    /**
     * @description returns the date format
     * @param locale locale to format
     */
    static getDateFormat(locale) {
        switch (locale || Utilities.getLocale()) {
            case FR_CA_LOCALE:
            case EN_CA_LOCALE:
                return DateFormatForLocale.CA_DATE_FORMAT;
            default:
                return DateFormatForLocale.EN_DATE_FORMAT;
        }
    }
    /**
     * @description returns the date by adjusting UTC offset
     * @param dateValue date to convert
     */
    static adjustUTCOffset(dateValue) {
        return dateValue
            ? moment(moment(new Date(dateValue))
                .utc()
                .format(DEFAULT_DATE_TIME_FORMAT)).toDate()
            : null;
    }
    /**
     * @description will retain whatever timezone offset is supplied.
     * @param datetimeValue date time string
     * @param format return date string format like 'LLL'
     */
    static parseZone(datetimeValue, format = DEFAULT_DATE_TIME_FORMAT) {
        return moment.parseZone(datetimeValue).format(format);
    }
    /**
     * To add the numberof days in current date
     * @param numberOfDays number of days to add
     */
    static addDaysToCurrentDate(numberOfDays) {
        return new Date(moment()
            .add(numberOfDays, DateFragmentType.DAYS)
            .calendar());
    }
    /**
     * To subtract the numberof days from current date
     * @param numberOfDays number of days to subtract
     */
    static subtractDaysFromCurrentDate(numberOfDays) {
        return new Date(moment()
            .subtract(numberOfDays, DateFragmentType.DAYS)
            .calendar());
    }
    /**
     * To add the numberof years in the selected date --
     * @param dateValue Date as of selected Date
     * @param numberOfYears number of years to add
     */
    static addYearsToDate(dateValue, numberOfYears) {
        return new Date(moment(dateValue)
            .add(numberOfYears, DateFragmentType.YEARS)
            .toDate());
    }
    /**
     * To add the numberof years in the selected date --
     * @param dateValue Date as of selected Date
     * @param numberOfYears number of years to subtract
     */
    static subtractYearsFromDate(dateValue, numberOfYears) {
        return new Date(moment(dateValue)
            .subtract(numberOfYears, DateFragmentType.YEARS)
            .toDate());
    }
    /**
     *
     * @param dateValue date time timestamp value
     * @returns returns the date as "h:mm a" if date is today,
     * 			returns "MMM DD, h:mm a" if date is same year,
     * 			returns MM/DD/YYYY if date is not the same year & returns YYYY/MM/DD for CA
     */
    static formatDateForTodayForCurrentYearForOtherDays(dateValue, dateFormat) {
        if (dateValue && DateUtilities.isToday(dateValue)) {
            return DateUtilities.formatDate(dateValue, LOCAL_TIME_FORMAT);
        }
        return DateUtilities.formatDateForAllDays(dateValue, dateFormat, true);
    }
    /**
     * @param dateValue date time value
     * @param dateFormat [optional] Date format
     * @param showDateTime [optional] flag to show/hide time for current year
     * @returns returns "MMM DD" if date is same year & returns "MMM DD, h:mm a" if date is same year and should show time,
     * 			returns MM/DD/YYYY if date is not the same year & returns YYYY/MM/DD for CA
     */
    static formatDateForAllDays(dateValue, dateFormat, showDateTime) {
        if (moment(DateUtilities.formatDate(dateValue, DATE_FORMAT_YYYY)).isSame(moment()
            .year()
            .toString(), 'year')) {
            return showDateTime
                ? DateUtilities.formatDate(dateValue, DATE_FORMAT_MMMM_DD_HH_MM_A)
                : DateUtilities.formatDate(dateValue, DATE_FORMAT_MMMM_DD);
        }
        return dateFormat === DateFormatForLocale.CA_DATE_FORMAT
            ? DateUtilities.formatDate(dateValue, DATE_FORMAT_CA_YYYY_MM_DD)
            : DateUtilities.formatDate(dateValue, DateFormatForLocale.EN_DATE_FORMAT);
    }
    /**
     * @description returns true if the date is a weekend (Saturday or Sunday)
     * @param date date to check
     */
    static isWeekend(date) {
        if (!DateUtilities.isValidDate(date))
            return false;
        const day = date.getDay();
        return day === 0 || day === 6;
    }
    /**
     * @description Returns the date after adding the specified number of years minus one day.
     * E.g. 1 Jan 2024 with 1 year returns 31 Dec 2024,
     *      29 Feb 2024 with 1 year returns 28 Feb 2025.
     * @param date The start date
     * @param numberOfYears The number of years to add
     * @returns The resulting date
     */
    static getDateAfterYears(date, numberOfYears) {
        return moment(date)
            .subtract(1, DateFragmentType.DAYS)
            .add(numberOfYears, DateFragmentType.YEARS)
            .toDate();
    }
}
/**
 * @description returns number of days for tooltip
 * @param dateValue date to use in calculation
 * @param translateFn function to use for translation
 */
DateUtilities.getNumberOfDays = (dateValue, translateFn = defaultTranslateFn) => {
    const days = DateUtilities.daysPastToday(dateValue);
    if (days === 0) {
        return translateFn('Today');
    }
    if (days > 0) {
        return days === 1 ? translateFn('Tomorrow') : translateFn(`in ${days} Days`);
    }
    else
        return days === -1 ? translateFn('Yesterday') : translateFn(`${Math.abs(days)} Days ago`);
};
/**
 * @description return boolean value to check if new date is same as current date
 * @param currentDate current date to use in calculation
 * @param newDate new select date to use in calculation
 */
DateUtilities.isSameDay = (currentDate, newDate) => {
    return (currentDate.getFullYear() === newDate.getFullYear() &&
        currentDate.getDate() === newDate.getDate() &&
        currentDate.getMonth() === newDate.getMonth());
};
/**
 * To subtract the number of days from current date (preserves current time)
 * @param numberOfDays number of days to subtract
 * @returns date after subtracting the number of days from current date with time
 */
DateUtilities.subtractDaysFromCurrentDateWithTime = (numberOfDays) => {
    return moment()
        .subtract(numberOfDays, DateFragmentType.DAYS)
        .toDate();
};
//# sourceMappingURL=DateUtilities.js.map